import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  projectsApi,
  ProjectCreateInput,
  ProjectDocumentItem,
  ProjectDocumentsResponse,
} from "@/services/projectsApi";

export function useProjects() {
  return useQuery({
    queryKey: ["projects"],
    queryFn: projectsApi.list,
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ["projects", id],
    queryFn: () => projectsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (project: ProjectCreateInput) => projectsApi.create(project),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof projectsApi.update>[1]) =>
      projectsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects", id] });
    },
  });
}

export function useInsertProjectDocuments() {
  const queryClient = useQueryClient();
  return useMutation<
    ProjectDocumentsResponse,
    Error,
    {
      projectId: string;
      documentIds: string[];
      customFieldValues?: Record<string, Record<string, unknown>>;
    }
  >({
    mutationFn: ({ projectId, documentIds, customFieldValues }) =>
      projectsApi.insertDocuments(projectId, documentIds, customFieldValues),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "documents"] });
    },
  });
}

export function useUpdateDocumentCustomFieldValues() {
  const queryClient = useQueryClient();
  return useMutation<
    ProjectDocumentItem,
    Error,
    {
      projectId: string;
      documentId: string;
      customFieldValues: Record<string, unknown>;
    }
  >({
    mutationFn: ({ projectId, documentId, customFieldValues }) =>
      projectsApi.updateDocumentCustomFieldValues(projectId, documentId, customFieldValues),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "documents", "detail"] });
    },
  });
}

export function useProjectDocuments(projectId: string | undefined) {
  return useQuery<ProjectDocumentItem[]>({
    queryKey: ["projects", projectId, "documents", "detail"],
    queryFn: () => {
      if (!projectId) return Promise.resolve([] as ProjectDocumentItem[]);
      return projectsApi.getProjectDocuments(projectId);
    },
    enabled: !!projectId,
    retry: false,
  });
}

export function useDeleteProjectDocuments() {
  return useMutation<
    ProjectDocumentsResponse,
    Error,
    { projectId: string; documentIds: string[] }
  >({
    mutationFn: ({ projectId, documentIds }) =>
      projectsApi.deleteDocuments(projectId, documentIds),
  });
}

export function useProjectDocumentIds(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "documents"],
    queryFn: () => {
      if (!projectId) return Promise.resolve([]);
      return projectsApi.getDocumentIds(projectId);
    },
    enabled: !!projectId,
    retry: false,
  });
}

export function useSyncProjectDocuments() {
  const queryClient = useQueryClient();
  return useMutation<
    ProjectDocumentsResponse,
    Error,
    { projectId: string; documentIds: string[] }
  >({
    mutationFn: ({ projectId, documentIds }) =>
      projectsApi.syncDocuments(projectId, documentIds),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "documents"] });
    },
  });
}

